import React, { useState, useEffect, useMemo, useCallback } from "react";

import { useTranslation } from "react-i18next";
import { useLoading } from "../../context/LoadingContext";
import { useAuth } from "../../context/AuthContext";

import { useNavigate } from "react-router-dom";

import TransactionStatus from "./TransactionStatus";

import { IoMdArrowBack } from "react-icons/io";
import { IoClose } from "react-icons/io5";
import { RiInformation2Fill } from "react-icons/ri";
import { ChevronRight } from "lucide-react";
import FloatingMenu from "../FloatingMenu";
import ErrorBanner from "../ui/ErrorBanner";

import {
    formatCurrency,
    restrictToDecimalNumbers,
    sanitizeDecimalInput
} from "../../utils/global";

import {
    updateTRCDeposit,
    fetchCryptoList,
    fetchDepositPromotionList,
    fetchCryptoPendingList,
    fetchCurrencyRate
} from "../../utils/api";
import { assetPath } from "../../utils/assets";

import config from '../../config';

const CryptoDeposit = () => {
    const { t } = useTranslation();
    const { setLoading } = useLoading();
    const { token } = useAuth();
    const navigate = useNavigate();

    const [error, setError] = useState({
        message: "",
        shake: false
    });
    const [modal, setModal] = useState({
        show: false,
        url: null,
    });
    const [coin, setCoin] = useState({
        list: [],
        selected: null,
        network: null
    });    
    const [deposit, setDeposit] = useState({
        amount: "",
        pending: null,
        options: ["10.00", "20.00", "50.00", "100.00", "200.00", "500.00"]
    });
    const [promotion, setPromotion] = useState({
        list: [],
        selected: null,
        confirmed: false
    });
    const [timer, setTimer] = useState({
        started: false,
        timeout: true,
        secondsLeft: null,
        lastApiParams: null
    });

    const [rate, setRate] = useState(0);

    const timeoutsec = 1800 - 5;

    const clearError = useCallback(() => {
        setError((prev) => ({ ...prev, message: "" }));
    }, []);

    const groupedCoins = useMemo(() => {
        const coinMap = new Map();
        coin.list.forEach((c) => {
            if (!coinMap.has(c.name)) {
                coinMap.set(c.name, {
                    name: c.name,
                    crypto_code: c.crypto_code,
                    icon: c.icon,
                    networks: [],
                });
            }
            coinMap.get(c.name).networks.push({
                id: c.id,
                symbol: c.symbol,
                min_deposit: c.min_deposit,
                max_deposit: c.max_deposit,
                deposit: c.deposit,
                icon: c.icon,
            });
        });
        return Array.from(coinMap.values());
    }, [coin.list]);
    
    const networkOptions = coin.selected
        ? groupedCoins.find((c) => c.name === coin.selected.name)?.networks || []
        : [];

    const checkMinMaxDeposit = useCallback((amount) => {
        if (isNaN(amount)) {
            clearError();
            return;
        }

        const minDeposit = 5;
        const maxDeposit = 1000;
        let message = "";

        if (amount < minDeposit) {
            message = t("minDeposit", { currency: config.CURRENCY_ISO_CODE, amount: formatCurrency(minDeposit) });
        } else if (amount > maxDeposit) {
            message = t("maxDeposit", { currency: config.CURRENCY_ISO_CODE, amount: formatCurrency(maxDeposit) });
        }

        setError(prev => prev.message === message ? prev : { ...prev, message, shake: !!message });

        if (message) {
            setCoin((prev) => ({ ...prev, selected: null, network: null }));
            setPromotion((prev) => ({ ...prev, selected: null, confirmed: null }));
            setTimeout(() => setError(prev => ({ ...prev, shake: false })), 500);
        }
    }, [t, clearError]);

    const handleDepositAmountChange = useCallback((input) => {
        const inputValue = input?.target ? input.target.value : input;
        const sanitizedValue = inputValue.replace(/[^0-9.]/g, '')
                                     .replace(/^(\d*\.\d*?)\..*$/, '$1');

        setDeposit(prev => ({ ...prev, amount: sanitizedValue }));

        const amount = Number(sanitizedValue);
        checkMinMaxDeposit(amount);
    }, [checkMinMaxDeposit]);
    
    const loadData = useCallback(async () => {
        setLoading(true);
        clearError();

        try {
            const [coinResponse, promotionResponse, cryptoPendingResponse, rateResponse] = await Promise.all([
                fetchCryptoList(),
                fetchDepositPromotionList(),
                fetchCryptoPendingList(),
                fetchCurrencyRate()
            ]);

            setCoin(prev => ({
                ...prev,
                list: coinResponse.success && Array.isArray(coinResponse.data) ? coinResponse.data : []
            }));

            if (coinResponse.success && Array.isArray(coinResponse.data) && coinResponse.data.length > 0) {
                const filteredCoins = coinResponse.data.filter((c) => c.deposit === 1);
                setCoin((prev) => ({ ...prev, list: filteredCoins }));
            } else {
                setCoin((prev) => ({ ...prev, list: [] }));
            }

            setDeposit(prev => ({
                ...prev,
                pending: cryptoPendingResponse.success ? cryptoPendingResponse.data : null
            }));

            if (cryptoPendingResponse.success && cryptoPendingResponse.data) {
                navigate("/crypto-address");
            }
            
            setPromotion(prev => ({
                ...prev,
                list: promotionResponse.success && Array.isArray(promotionResponse.data) ? promotionResponse.data : []
            }));

            if (rateResponse.success) {
                setRate(rateResponse.data.exchange_rate);
            } else {
                setRate(0);
            }
        } catch (error) {
            setError(prev => ({ ...prev, message: t("pleaseTryLater") }));
        } finally {
            setLoading(false);
        }
    }, [t, setLoading, clearError, navigate]);

    const handleApi = useCallback(
        async (params) => {
            setLoading(true);
            clearError();
            setModal((prev) => ({ ...prev, loading: true }));

            try {
                let response;
                if (params.coin?.name === "USDT" && params.network?.symbol?.toLowerCase().includes("trc")) {
                    response = await updateTRCDeposit(params);
                }

                if (response.success) {
                    const address = response?.address || response?.data?.address;
                    if (address) {
                        navigate("/crypto-address", { state: { address, amount: params.amount } });
                        return;
                    }
                    if (params.coin?.name === "USDT" && params.network?.symbol?.toLowerCase().includes("trc")) {
                        navigate("/crypto-address");
                    } else {
                        const url = response?.data;
                        if (url) {
                            const timeoutSec = timeoutsec;
                            setTimer((prev) => ({
                                ...prev,
                                lastApiParams: params,
                                secondsLeft: timeoutSec,
                                started: timeoutSec !== null,
                                timeout: false,
                            }));
                            setModal((prev) => ({ ...prev, url, show: true, loading: false }));
                        } else {
                            setError(prev => ({ ...prev, message: t("linkNotAvailable") }));
                        }
                    }
                } else {
                    setError(prev => ({ ...prev, message: t(response.data) || t("pleaseTryLater") }));
                }
            } catch (err) {
                setError(prev => ({ ...prev, message: t("failedToFetchLink") }));
            } finally {
                setLoading(false);
            }
        },
        [t, setLoading, clearError, navigate, timeoutsec]
    );

    const handleSubmit = useCallback(() => {
        const currentParams = {
            promotion: promotion.confirmed,
            coin: coin.selected,
            network: coin.network,
            amount: deposit.amount
        };

        const timerIsOut = timer.timeout;
        const channelChanged = timer.lastApiParams?.network?.id !== currentParams.network?.id ||
            timer.lastApiParams?.promotion?.id !== currentParams.promotion?.id;

        if (timerIsOut || channelChanged || !timer.lastApiParams) {
            handleApi(currentParams);
        } else {
            setModal((prev) => ({ ...prev, show: true }));
        }
    }, [handleApi, promotion.confirmed, coin.selected, coin.network, deposit.amount, timer.timeout, timer.lastApiParams]);

    useEffect(() => {
        if (error.message) {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [error.message]);

    useEffect(() => {
        if (token) {
            loadData();
        }
    }, [token, loadData]);

    useEffect(() => {
        if (!timer.started || timer.secondsLeft <= 0) return;

        const id = setInterval(() => {
            setTimer(prev => {
                if (prev.secondsLeft <= 1) {
                    return { ...prev, secondsLeft: 0, timeout: true, started: false };
                }
                return { ...prev, secondsLeft: prev.secondsLeft - 1 };
            });
        }, 1000);

        return () => clearInterval(id);
    }, [timer.started, timer.secondsLeft]);

    return (
        <section className="page-shell">
            {!modal.show && (
                <div className="page-header">
                    <div
                        className="cursor-pointer bg-secondary container-back-button text-color-blue rounded-full"
                        onClick={() => navigate("/deposit")}
                    >
                        <IoMdArrowBack className="size-sm" />
                        <span className="text-sm">
                            {t("backToLobby")}
                        </span>
                    </div>
                    <button className="cursor-pointer" onClick={() => navigate("/")}>
                        <IoClose className="size-lg text-color-blue" />
                    </button>
                </div>
            )}

            <ErrorBanner message={error.message} />

            <div className="px-4 pt-[30px] pb-[100px]">
                <p className="mb-3 font-bold text-sm text-white">
                    {t("Deposit")}
                </p>

                <div className="info-banner mb-8">
                    <RiInformation2Fill className="size-[30px] text-border-yellow" />
                    <div className="flex flex-col flex-grow">
                        <span className="text-xs text-white">
                            {t("minDeposit5USDT")}.
                        </span>
                    </div>
                </div>

                <div className="bg-main-gradient rounded-lg p-4 flex flex-col mt-[40px]">
                    <span className="text-xs text-white mb-3">
                        {t("cryptoRateInfo")}
                    </span>
                    <div className="bg-secondary container-equal-lg rounded-md relative flex flex-col items-start">
                        <span className="text-sm text-color-blue">
                            {t("currentRate")}:
                        </span>
                        <span className="text-sm text-white font-bold">
                            1 {t("USDT")} = {config.CURRENCY_ISO_CODE} {formatCurrency(rate)}
                        </span>
                    </div>
                </div>
                
                <div className="max-w-md my-6 text-center mx-auto w-fit">
                    <div
                        className={`text-[36px] font-bold text-white transition-transform duration-300 ${
                            error.shake ? "animate-shake" : ""
                        }`}
                    >
                        <input
                            type="text"
                            value={deposit.amount || ""}
                            onKeyDown={restrictToDecimalNumbers}
                            onChange={handleDepositAmountChange}
                            onInput={sanitizeDecimalInput}
                            placeholder="0"
                            className="bg-transparent text-white text-[36px] font-bold text-right outline-none pr-2 w-auto min-w-[2ch] max-w-[10ch] md:max-w-[20ch]"
                            style={{ width: `${(deposit.amount?.length || 1) + 1}ch` }}
                        />
                        <span className="text-white/70 pl-1">USDT</span>
                    </div>
                </div>

                <div className="grid grid-cols-3 gap-3 my-8">
                    {deposit.options.map((amount, index) => (
                        <div
                            key={index}
                            className="relative"
                        >
                            {amount === "50.00" && (
                                <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-2 py-1 text-xxs font-bold text-black uppercase rounded-full btn-gradient w-max">
                                    {t("popular")}
                                </div>
                            )}
                            <div
                                onClick={() => {
                                    handleDepositAmountChange(amount);
                                }}
                                className={`cursor-pointer p-2 h-[36px] text-center transition-colors rounded-sm ${amount === `${deposit.amount}` ? "btn-gradient text-black" : "bg-main-gradient text-white"}`}
                            >
                                <span className="text-sm font-bold">
                                    {formatCurrency(amount)}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>

                <div
                    className={`transition-all duration-300 ease-in-out ${
                        !error.message && deposit.amount
                            ? "opacity-100 translate-y-0 scale-100"
                            : "opacity-0 translate-y-4 scale-95 pointer-events-none"
                    }`}
                >
                    <div className="mb-8">
                        <p className="mb-3 text-sm font-bold text-white">
                            {t("selectCoin")}
                        </p>

                        <div className="rounded-lg bg-option-blue">
                            {groupedCoins.map((c) => (
                                <div
                                    key={c.name}
                                    className={`container-xs justify-start gap-2 cursor-pointer text-white 
                                        border-b border-option-selected-blue first:rounded-t-lg last:rounded-b-lg last:border-b-0
                                        ${coin.selected?.name === c.name ? "bg-option-second-selected-blue" : ""}`}
                                    onClick={() => {
                                        setCoin((prev) => ({ ...prev, selected: prev.selected?.name === c.name ? null : c, network: null }));
                                        setPromotion((prev) => ({ ...prev, selected: null, confirmed: null }));
                                        clearError();
                                    }}
                                >
                                    <img
                                        src={`${config.APP_URL}${c.icon}`}
                                        alt={`${c.name} icon`}
                                        className="size-xl"
                                    />
                                    <div className="flex flex-col">
                                        <span className="text-sm">
                                            {c.name}
                                        </span>
                                        <span className="text-xs text-color-blue">
                                            {c.crypto_code}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div
                    className={`transition-all duration-300 ease-in-out ${
                        !error.message && coin.selected && networkOptions.length > 0
                            ? "opacity-100 translate-y-0 scale-100"
                            : "opacity-0 translate-y-4 scale-95 pointer-events-none"
                    }`}
                >
                    <div className="mb-8">
                        <p className="mb-3 text-sm font-bold text-white">
                            {t("selectNetwork")}
                        </p>

                        <div className="rounded-lg bg-option-blue">
                            {networkOptions.map((n) => (
                                <div
                                    key={n.id}
                                    className={`container-equal-2xl justify-start gap-2 cursor-pointer text-white 
                                        border-b border-option-selected-blue first:rounded-t-lg last:rounded-b-lg last:border-b-0
                                        ${coin.network?.id === n.id ? "bg-option-second-selected-blue" : ""}`}
                                    onClick={() => {
                                        setCoin((prev) => ({ ...prev, network: prev.network?.id === n.id ? null : n }));
                                        setPromotion((prev) => ({ ...prev, selected: null, confirmed: null }));
                                        clearError();
                                    }}
                                >
                                    <span className="text-sm">
                                        {n.symbol}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div
                    className={`transition-all duration-300 ease-in-out ${
                        !error.message && coin.selected && coin.network
                            ? "opacity-100 translate-y-0 scale-100"
                            : "opacity-0 translate-y-4 scale-95 pointer-events-none"
                    }`}
                >
                    <div className="mb-8">
                        <p className="mb-3 text-sm font-bold text-white">
                            {t("selectPromotion")}
                        </p>

                        {promotion.list.length > 0 && (
                            <div className="overflow-hidden">
                                {promotion.list.map((promo) => (
                                    <div key={promo.id}>
                                        <div
                                            className={`container-promotion-button rounded-sm bg-dropdown-bg flex-between ${promotion.selected?.id === promo.id ? "rounded-b-none" : "mb-3"}`}
                                            onClick={() => {
                                                setPromotion(prev => {
                                                    const newSelection = prev.selected?.id === promo.id ? null : promo;
                                                    return { ...prev, selected: newSelection, confirmed: null };
                                                });
                                                clearError();
                                            }}
                                        >
                                            <div className="flex-center gap-3">
                                                <img
                                                    src={assetPath("/icons/gift.png")}
                                                    alt="Gift"
                                                    className="size-md"
                                                />
                                                <span className="text-sm text-white">
                                                    {promo.name}
                                                </span>
                                            </div>
                                            <div className="flex-center gap-3">
                                                <ChevronRight
                                                    className={`size-sm text-white animate-rotate ${promotion.selected?.id === promo.id ? "rotate-[90deg]" : ""}`}
                                                />
                                            </div>
                                        </div>

                                        {promotion.selected?.id === promo.id && (
                                            <div className="flex flex-col p-4 mb-3 bg-main-gradient animate-fadeIn last:rounded-b-sm">
                                                <div className="flex flex-col p-3 mb-3 bg-secondary rounded-lg">
                                                    <span className="text-sm text-white">
                                                        <span className="font-bold">{t("bonusAward")} </span>
                                                        : {promotion.selected.bonus_type === 1 ? promotion.selected.percentage+'%' : `${config.CURRENCY_ISO_CODE} ${formatCurrency(promotion.selected.amount)}`}
                                                    </span>
                                                    <br />

                                                    <span className="text-sm text-white">
                                                        <span className="font-bold">{t("turnOver")} </span>
                                                        : x{promotion.selected.turnover_multiply}
                                                    </span>
                                                    <br />

                                                    <span className="text-sm text-white">
                                                        <span className="font-bold">{t("minimumDeposit")} </span>
                                                        : {config.CURRENCY_ISO_CODE} {formatCurrency(promotion.selected.min_deposit)}
                                                    </span>
                                                    <br />

                                                    <span className="text-sm text-white">
                                                        <span className="font-bold">{t("maximumBonus")} </span>
                                                        : {promotion.selected.bonus_type === 1
                                                        ? `${config.CURRENCY_ISO_CODE} ${formatCurrency(promotion.selected.max_bonus_limit)}`
                                                        : '-'}
                                                    </span>
                                                    <br />

                                                    <span className="text-sm text-white">
                                                        {t("promotionTNC")}
                                                    </span>
                                                </div>

                                                <button
                                                    className={`container-sm rounded-sm ${promotion.confirmed && promotion.confirmed !== "skip" && promotion.confirmed?.id === promo.id ? 'bg-promotion-selected' : 'btn-gradient'}`}
                                                    onClick={() => {
                                                        setPromotion(prev => {
                                                            if (!prev.selected) return prev;
                                                            const isSame = prev.confirmed && prev.confirmed.id === prev.selected.id;
                                                            return { ...prev, confirmed: isSame ? null : prev.selected };
                                                        });
                                                        clearError();
                                                    }}
                                                >
                                                    <span
                                                        className={`text-sm text-center font-bold ${promotion.confirmed && promotion.confirmed !== "skip" && promotion.confirmed?.id === promo.id ? 'text-white' : ''}`}
                                                    >
                                                        {promotion.confirmed && promotion.confirmed !== "skip" && promotion.confirmed?.id === promo.id ? t("promotionSelected") : t("selectThisPromotion")}
                                                    </span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        <div
                            className={`container-promotion-button rounded-sm bg-dropdown-bg transition-all duration-300 ease-in-out flex-between 
                                ${promotion.selected === "skip" ? "container-promotion-button-border bg-secondary border-2 border-dropdown-bg" : ""}
                            `}
                            onClick={() => {
                                setPromotion(prev => ({
                                    ...prev,
                                    selected: prev.selected === "skip" ? null : "skip",
                                    confirmed: prev.confirmed === "skip" ? null : "skip"
                                }));
                                clearError();
                            }}
                        >
                            <div className="flex-center gap-3">
                                <img
                                    src={assetPath("/icons/skip_promo.png")}
                                    alt="skip"
                                    className="size-md"
                                />
                                <span
                                    className={`text-sm text-white ${promotion.selected === "skip" ? "font-bold" : ""}`}
                                    >
                                    {t("skipPromotion")}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <button
                    className={`fixed bottom-[60px] md:bottom-0 left-0 right-0 container-lg h-[60px] text-center btn-gradient transition-all duration-300 ease-in-out ${
                        !error.message && coin.selected && coin.network && promotion.confirmed
                            ? "opacity-100 translate-y-0 scale-100"
                            : "opacity-0 translate-y-4 scale-95 pointer-events-none"
                    }`}
                    onClick={() => {
                        handleSubmit();
                    }}
                >
                    <span className="text-sm font-bold">
                        {t("submit")}
                    </span>
                </button>
            </div>

            {modal.show && (
                <div className="modal-overlay">
                    <div className="sticky top-0 z-200 flex-between bg-fourth px-4 py-5">
                        <div
                            className="cursor-pointer bg-secondary container-back-button text-color-blue rounded-full"
                            onClick={() => {
                                setModal(prev => ({ ...prev, show: false }));
                            }}
                        >
                            <IoMdArrowBack className="size-sm" />
                            <span className="text-sm">
                                {t("backToLobby")}
                            </span>
                        </div>
                        <button className="cursor-pointer" onClick={() => navigate("/")}>
                            <IoClose className="size-lg text-color-blue" />
                        </button>
                    </div>

                    {!timer.timeout && (
                        <iframe
                            src={modal.url}
                            title={t("externalPayment")}
                            className="w-full h-screen border-none"
                        />
                    )}

                    {timer.timeout && (
                        <section className="min-h-screen bg-secondary">
                            <TransactionStatus
                                status='warning'
                                type="deposit"
                                title='timerEnded'
                                message='paymentQrCodeExpired'
                            />
                        </section>
                    )}
                </div>
            )}

            <FloatingMenu />
        </section>
    );
};

export default CryptoDeposit;